import _extends from "@babel/runtime/helpers/extends";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.function.name.js";
import "core-js/modules/es.array.map.js";
import React from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EmptyStateController from 'components/EmptyStateController';
import Button from 'components/Button';
import Flag from 'components/Flag';
import Heading from 'components/Heading';
import PageSection from 'components/PageSection';
import Landmark from 'components/Landmark';
import CollectionGrid, { CollectionGridItem } from 'components/CollectionGrid';
import useAriaFeedProps from 'hooks/useAriaFeedProps';
import PageTitle from 'components/PageTitle';
import SmartContentCard from '../../shared/smartContentCard';
import TopicList from './TopicList';
import Breadcrumbs from './breadcrumbs';
import { getCardTitleId, getCardDescriptionId } from 'components/ContentCards/shared';
import useBackLink from 'hooks/useBackLink';
import { useFetchBrowseTopics } from 'actions/topics';
import { useFetchMyBrowsePlaylists } from 'actions/playlists';
import LoadMoreOnScroll from 'components/LoadMoreOnScroll';
function BreadcrumbWidget(_ref) {
  var topic = _ref.topic,
    rootTitle = _ref.rootTitle;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var hasParent = topic === null || topic === void 0 ? void 0 : topic.parent_id;
  var parentUrl = hasParent ? "/browse/".concat(topic.parent_id) : '/browse';
  var breadcrumbs = [{
    title: rootTitle,
    link: '/browse',
    key: 'root'
  }, hasParent && {
    title: topic.parent_name,
    link: parentUrl,
    key: topic.parent_id
  }, (topic === null || topic === void 0 ? void 0 : topic.id) && {
    title: topic.name,
    key: topic.id
  }].filter(Boolean);
  var backLink = useBackLink({
    fallback: parentUrl
  });
  return /*#__PURE__*/React.createElement(Flag, {
    token: /*#__PURE__*/React.createElement(Button, {
      round: true,
      as: NavLink,
      to: backLink,
      icon: "arrow",
      iconOnly: true
    }, t('actions_back'))
  }, /*#__PURE__*/React.createElement(Breadcrumbs, {
    items: breadcrumbs
  }));
}
var PLAYLIST_HEADING_ID = 'playlist_heading';
export default function Browse() {
  var _useParams = useParams(),
    topicId = _useParams.topicId;
  var _useTranslation2 = useTranslation(),
    t = _useTranslation2.t;
  var isRoot = !topicId;

  // Make a request to get topics
  var _useFetchBrowseTopics = useFetchBrowseTopics({
      topicId: topicId
    }),
    _useFetchBrowseTopics2 = _useFetchBrowseTopics.data,
    data = _useFetchBrowseTopics2 === void 0 ? {
      subtopics: [],
      topic: null
    } : _useFetchBrowseTopics2;
  var subtopics = data.subtopics,
    topic = data.topic;
  var hasSubtopics = subtopics.length > 0;
  var _useFetchMyBrowsePlay = useFetchMyBrowsePlaylists({
      topic: topicId
    }),
    _useFetchMyBrowsePlay2 = _useFetchMyBrowsePlay.items,
    playlists = _useFetchMyBrowsePlay2 === void 0 ? [] : _useFetchMyBrowsePlay2,
    isLoading = _useFetchMyBrowsePlay.isLoading,
    isFetching = _useFetchMyBrowsePlay.isFetching,
    hasNextPage = _useFetchMyBrowsePlay.hasNextPage,
    isFetchingNextPage = _useFetchMyBrowsePlay.isFetchingNextPage,
    fetchNextPage = _useFetchMyBrowsePlay.fetchNextPage;
  var isLoadingMoreItems = isFetching && !isLoading;
  var _useAriaFeedProps = useAriaFeedProps({
      isLoading: isLoadingMoreItems
    }),
    getWrapperProps = _useAriaFeedProps.getWrapperProps,
    getItemProps = _useAriaFeedProps.getItemProps;
  var pageTitle = t('navbar_browse');
  var emptyContent = /*#__PURE__*/React.createElement(Landmark, {
    id: "empty_state_browse_page",
    icon: "asset-image"
  }, /*#__PURE__*/React.createElement("strong", null, t('error_empty')));
  var errorContent = /*#__PURE__*/React.createElement(Landmark, {
    icon: "asset-image"
  }, /*#__PURE__*/React.createElement("strong", null, t('error_404')));
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PageSection, null, isRoot ? /*#__PURE__*/React.createElement(PageTitle, {
    size: "large"
  }, pageTitle) : /*#__PURE__*/React.createElement(BreadcrumbWidget, {
    topic: topic,
    rootTitle: pageTitle
  })), hasSubtopics && /*#__PURE__*/React.createElement(PageSection, null, /*#__PURE__*/React.createElement(Heading, {
    as: "h2",
    mb: "s"
  }, t('content_type_topic_plural')), /*#__PURE__*/React.createElement(TopicList, {
    items: subtopics
  })), /*#__PURE__*/React.createElement(PageSection, null, /*#__PURE__*/React.createElement(Heading, {
    as: "h2",
    mb: "s",
    id: PLAYLIST_HEADING_ID
  }, t('content_type_collections_and_playlists')), /*#__PURE__*/React.createElement(EmptyStateController, {
    isEmpty: playlists.length === 0,
    emptyContent: emptyContent,
    errorContent: errorContent,
    isLoading: isLoading
  }, /*#__PURE__*/React.createElement(CollectionGrid, getWrapperProps({
    labelledBy: PLAYLIST_HEADING_ID
  }), playlists.map(function (playlist, index) {
    var id = playlist.id;
    var titleId = getCardTitleId(id);
    var descriptionId = getCardDescriptionId(id);
    return /*#__PURE__*/React.createElement(CollectionGridItem, _extends({
      key: playlist.id,
      id: "playlist_card_".concat(id)
    }, getItemProps({
      index: index,
      labelledBy: titleId,
      describedBy: descriptionId
    })), /*#__PURE__*/React.createElement(SmartContentCard, {
      content: playlist,
      isFeedItem: true,
      useCustomId: true
    }));
  })), /*#__PURE__*/React.createElement(LoadMoreOnScroll, {
    hasNextPage: hasNextPage,
    isFetchingNextPage: isFetchingNextPage,
    onFetchNextPage: fetchNextPage
  })), isLoadingMoreItems && /*#__PURE__*/React.createElement(Landmark, {
    icon: "spinner"
  })));
}